import { useQuery } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import { getQueryKey } from '@/api/utils/getQueryKey';
import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

export type ApiRequest_Get_Group_Negotiation = {
  communityId: number;
  negotiationId: number;
};

export type GroupNegotiationMember = {
  id: number;
  member_id: number;
  joined_at: string;
  user: {
    id: string;
    full_name: string;
    email: string;
  };
};

export type ApiResponse_Get_Group_Negotiation = {
  id: number;
  title: string;
  description?: string;
  status: string;
  created_at: string;
  updated_at: string;
  closed_at?: string;
  end_date?: string;
  metadata?: Record<string, unknown>;
  initiator: {
    id: number;
    user_id: string;
    full_name: string;
    email: string;
  };
  members: GroupNegotiationMember[];
};

const METHOD = 'GET';
const ENDPOINT = 'communities/{communityId}/group-negotiations/{negotiationId}';

export const QUERY_KEY = (params: ApiRequest_Get_Group_Negotiation) =>
  getQueryKey(
    METHOD,
    getResolvedEndpoint(ENDPOINT, { 
      communityId: params.communityId.toString(),
      negotiationId: params.negotiationId.toString(),
    }),
    {}
  );

export const useGetGroupNegotiation = (
  params: ApiRequest_Get_Group_Negotiation,
  enabled = true
) => {
  const { session } = useAuth();

  return useQuery({
    enabled: enabled && !!session?.access_token && !!params.communityId && !!params.negotiationId,
    queryKey: QUERY_KEY(params),
    queryFn: async () => {
      try {
        const data = await handleApiCall<
          ApiResponse_Get_Group_Negotiation,
          Record<string, never>
        >(
          session,
          METHOD,
          getResolvedEndpoint(ENDPOINT, { 
            communityId: params.communityId.toString(),
            negotiationId: params.negotiationId.toString(),
          }),
          {}
        );

        return data;
      } catch (err) {
        enqueueSnackbar('Failed to fetch save together group details.', { variant: 'error' });
        throw err;
      }
    },
  });
};